/**
 * Google Analytics 4 with Consent Mode v2.
 *
 * Privacy posture: gtag.js is NOT loaded until the visitor accepts analytics in
 * the consent banner (or has previously accepted). No third-party request is
 * made before consent. This is a documented deviation from the spec's
 * "no third-party scripts" default — see docs/design-plan.md §8.1.
 *
 * The CSP in staticwebapp.config.json allows only googletagmanager.com (script)
 * and the google-analytics domains (connect/img) — nothing else.
 */

const GA_ID = import.meta.env.VITE_GA_ID;
export const CONSENT_KEY = "analytics-consent"; // "granted" | "denied"

let initialized = false;

function loadGtag(): void {
  if (initialized || !GA_ID) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  // gtag must push `arguments`, so it is a classic function, not an arrow.
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;

  // Consent granted at this point (we only load after acceptance).
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
}

/** Read the stored consent decision, if any. */
export function getStoredConsent(): "granted" | "denied" | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Call once on app start: respects a previously granted decision. */
export function initAnalyticsFromStorage(): void {
  if (getStoredConsent() === "granted") loadGtag();
}

/** Banner "Accept" handler. */
export function grantConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, "granted");
  } catch {
    /* storage unavailable — analytics simply won't persist */
  }
  loadGtag();
}

/** Banner "Decline" handler. */
export function denyConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, "denied");
  } catch {
    /* ignore */
  }
}

/** Whether analytics is even configured (a GA id is present at build time). */
export const analyticsConfigured = Boolean(GA_ID);
